'use client';

import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { SmartLink } from '@/components/smart-link';
import { Combobox } from '@/components/ui/combobox';
import { LocationsMap } from '@/components/locations-map';
import type { LocationEntry } from '@/lib/location-geo';

const ALL = 'all';

interface NearCity {
  slug: string;
  title: string;
  image: string | null;
  miles: number;
}

/**
 * The locations index: search, a searchable state dropdown, and a card grid.
 * When we can place the visitor and no filter is active, their nearest city
 * leads the grid as a large 2×3 feature tile in the top-left. The state
 * dropdown is the text counterpart to the map (Phase 2), which drives the same
 * `state` filter.
 */
export function LocationsExplorer({
  entries,
  states,
}: {
  entries: readonly LocationEntry[];
  states: readonly { code: string; name: string; count: number }[];
}) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<string>(ALL);
  const [near, setNear] = useState<NearCity | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/near-you${window.location.search}`, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { city: NearCity | null } | null) => {
        if (data?.city) setNear(data.city);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const codeByName = useMemo(() => new Map(states.map((s) => [s.name, s.code])), [states]);
  const nameByCode = useMemo(() => new Map(states.map((s) => [s.code, s.name])), [states]);
  const servedCounts = useMemo(() => new Map(states.map((s) => [s.code, s.count])), [states]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (state === ALL || entry.state === state) &&
          (!q || entry.title.toLowerCase().includes(q) || entry.stateName.toLowerCase().includes(q)),
      ),
    [entries, state, q],
  );

  // The feature tile only makes sense on the unfiltered, unsearched view.
  const featured = state === ALL && !q ? near : null;
  const cards = featured ? filtered.filter((entry) => entry.slug !== featured.slug) : filtered;
  const featuredCityName = featured ? (featured.title.split(',')[0]?.trim() ?? featured.title) : '';

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <label htmlFor="location-search" className="sr-only">
          Search locations
        </label>
        <input
          id="location-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by city or state…"
          className="w-full max-w-md rounded-full border border-brand-navy/15 bg-white px-5 py-3 text-[15px] text-brand-navy placeholder:text-brand-navy/70 transition-shadow outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/10"
        />
        <p className="text-sm font-medium whitespace-nowrap text-brand-navy/70">
          {filtered.length} {filtered.length === 1 ? 'city' : 'cities'}
          {state !== ALL ? ` in ${nameByCode.get(state) ?? state}` : ''}
        </p>
        <div className="w-full sm:ml-auto sm:w-64">
          <Combobox
            name="state-filter"
            groups={[{ group: 'States we serve', options: states.map((s) => s.name) }]}
            allowEmpty
            placeholder="All states"
            value={state === ALL ? '' : (nameByCode.get(state) ?? '')}
            onValueChange={(value) => setState(value ? (codeByName.get(value) ?? ALL) : ALL)}
          />
        </div>
      </div>

      {featured ? (
        <SmartLink
          href={`/featured-locations/${featured.slug}`}
          className="group mt-8 grid overflow-hidden rounded-3xl border border-brand-navy/5 bg-white shadow-lg shadow-brand-navy/10 transition-all hover:-translate-y-1 hover:shadow-xl md:grid-cols-2"
        >
          {featured.image ? (
            <Image
              src={featured.image}
              width={800}
              height={600}
              alt={featured.title}
              sizes="(min-width: 768px) 50vw, calc(100vw - 48px)"
              quality={70}
              className="aspect-[16/10] w-full object-cover md:aspect-auto md:h-full"
            />
          ) : (
            <div className="aspect-[16/10] w-full bg-brand-royal/10 md:h-full" />
          )}
          <div className="flex flex-col justify-center gap-3 p-8 md:p-10">
            <p className="eyebrow text-brand-blue">We&rsquo;re near you</p>
            <h2 className="heading-section text-brand-royal">We&rsquo;re in {featuredCityName}</h2>
            <p className="text-brand-navy/75">
              We run team building and corporate events in {featured.title}, every program
              delivered on site with no travel costs.
            </p>
            <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-brand-yellow px-6 py-3 text-[15px] font-bold text-brand-navy transition-transform group-hover:translate-x-0.5">
              View {featuredCityName} <span aria-hidden>→</span>
            </span>
          </div>
        </SmartLink>
      ) : null}

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="flex flex-col rounded-2xl border border-brand-navy/5 bg-white p-4 shadow-sm sm:col-span-2 sm:row-span-2">
          <LocationsMap servedCounts={servedCounts} selected={state} onSelect={setState} />
          <p className="mt-1 text-center text-xs text-brand-navy/70">
            Tap a state to see its cities. Also serving{' '}
            <SmartLink href="/featured-locations/toronto-on" className="font-semibold text-brand-blue hover:underline">
              Toronto, Canada
            </SmartLink>
            .
          </p>
        </div>

        {cards.map((location) => (
          <SmartLink
            key={location.slug}
            href={`/featured-locations/${location.slug}`}
            className="group overflow-hidden rounded-2xl border border-brand-navy/5 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
          >
            {location.image ? (
              <Image
                src={location.image}
                width={640}
                height={360}
                alt={location.title}
                sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, calc(100vw - 48px)"
                quality={65}
                className="aspect-[16/9] w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="aspect-[16/9] w-full bg-brand-navy/5" />
            )}
            <div className="flex items-center justify-between p-4">
              <h3 className="text-lg font-bold text-brand-navy group-hover:text-brand-blue">{location.title}</h3>
              <span aria-hidden className="text-brand-blue">
                →
              </span>
            </div>
          </SmartLink>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="mt-10 text-center text-brand-navy/70">
          No cities match, but we deliver nationwide. Try another state or{' '}
          <SmartLink href="/get-quote" className="font-semibold text-brand-blue underline">
            get a quote
          </SmartLink>
          .
        </p>
      ) : null}
    </div>
  );
}
